// @ts-strict-ignore
import { AfterViewChecked, AfterViewInit, Component, Input, NgZone, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { LMContextService } from '@services/lm-context.service';
import { LMLplEditorService } from './lm-lpl-editor.service';
import { LMFormRequestService } from '@form/services';
import { LMUrlUtilService } from '@app/services';
import { SohoAlertDirective, SohoSwapListComponent } from 'ids-enterprise-ng';
import { LMGlobalPersonalizationService } from '@app/states';
import { LMFilterBuilderComponent } from '@modal/filter-builder/lm-filter-builder.component';
import { LMFilterBuilderService } from '@modal/filter-builder';
import { Observable, of } from "rxjs";

@Component({
  selector: 'lm-repository-helper',
  templateUrl: 'lm-repository-helper.component.html',
  styleUrls: [ 'lm-repository-helper.component.scss' ],
})

export class LMRepositoryHelperComponent implements OnInit {
  @ViewChild(LMFilterBuilderComponent) filterBuilder: LMFilterBuilderComponent;
  @ViewChild(SohoSwapListComponent) swapListComponent: SohoSwapListComponent;
  @ViewChild('previewComponent', { read: SohoAlertDirective }) generatedLplAlert: SohoAlertDirective;

  @Input() options: LMRepositoryHelperOptions;

  useCompactFormLayout = false;
  ruleType: SecClassRuleType;
  ruleTypeObjects: any[];
  securableType: SecClassType
  securableTypeObjects: any[];
  hasContainment: boolean;
  containment: string;
  containmentLabel: string
  containmentObjects: any[];
  secClassType: SecClassType;
  snippet: string;
  snippetObjects: any[];

  securableObjectLabel: string;
  securableObject: SecClassObject;
  securableObjects: any[];
  availableItems: Array<SohoSwapListItem> = [];
  currentItems: Array<SohoSwapListItem> = [];
  availableItemsHeader;
  selectedItemsHeader
  preview: string;
  fieldLookupUrl: string;
  fieldSearchUrl: string;
  lplValidationUrl: string

  constructor(
    protected ngZone: NgZone,
    protected lmContextService: LMContextService,
    protected lmGlobalPersonalizationService: LMGlobalPersonalizationService,
    protected lmLplEditorService: LMLplEditorService,
    protected lmUrlUtilService: LMUrlUtilService,
    protected lmFormRequestService: LMFormRequestService,
    protected lmFilterBuilderService: LMFilterBuilderService) {
  }

  ngOnInit(): void {
    this.initializeVariables();
  }

  securableObjectSource = (response: any, searchTerm: any) => {
    response(this.securableObjects, true);
  }

  containmentSource = (response: any, searchTerm: any) => {
    response(this.containmentObjects, true);
  }

  onSnippetRuleTypeChange() {
    this.buildSnippets();
    this.snippet = this.snippetObjects[0].value;
    this.rebuildPreview();
  }

  onActionRuleTypeChange(initializing?: boolean) {
    this.buildSnippets();

    if (initializing) {
      const securableTypeCtx = this.options?.rule?.ruleTypeName as SecClassType;
      this.snippet = securableTypeCtx;
    }

    if (!this.snippet) {
      this.snippet = this.snippetObjects[0].value;
    }

    this.onSnippetChange(initializing);
  }

  onSnippetChange(initializing?: boolean) {
    this.hasContainment = false;
    this.containment = undefined;

    if (this.options?.request === 'snippet') {
      this.rebuildPreview();
    } else if (this.options?.request === 'actions') {
      if (this.ruleType === 'object') {
        if (this.snippet === 'BusinessClass' || this.snippet === 'KeyField' || this.snippet === 'Field' || this.snippet === 'AllFields') {
          this.hasContainment = true;
          this.containmentLabel = Soho.Locale.translate('LPL_EDITOR_BusinessClass');
          this.lmLplEditorService.getSecurableObjectsList('BusinessClass', this.containment).subscribe((response: any) => {
            this.updateContainmentObjects(response, initializing);
            this.onActionContainmentChange();
          });
        } else if (this.snippet === 'BusinessTask') {
          this.hasContainment = true;
          this.containmentLabel = Soho.Locale.translate('LPL_EDITOR_BusinessTask');
          this.lmLplEditorService.getSecurableObjectsList(this.snippet, this.containment).subscribe((response: any) => {
            this.updateContainmentObjects(response, initializing);
            this.onActionContainmentChange();
          });
        } else {
          this.lmLplEditorService.getSecurableActionsList('Type', this.snippet).subscribe((response: any) => {
            this.updateSwapList(response);
            this.rebuildPreview();
          });
        }
      } else {
        this.lmLplEditorService.getSecurableActionsList('Type', this.snippet).subscribe((response: any) => {
          this.updateSwapList(response);
          this.rebuildPreview();
        });
      }
    }
  }

  onObjectTypeChange() {
    this.hasContainment = false;
    this.containment = undefined;
    this.securableObject = undefined;
    this.containmentObjects = [];
    this.securableObjects = [];

    if (this.securableType === 'Field') {
      this.hasContainment = true;
      this.containmentLabel = Soho.Locale.translate('LPL_EDITOR_BusinessClass');
      this.lmLplEditorService.getSecurableObjectsList('BusinessClass', this.containment).subscribe((response: any) => {
        this.updateContainmentObjects(response);
        this.onObjectContainmentChange();
      });
    } else if (this.securableType === 'MenuItem') {
      this.hasContainment = true;
      this.containmentLabel = Soho.Locale.translate('LPL_EDITOR_Menu');
      this.lmLplEditorService.getSecurableObjectsList('Menu', this.containment).subscribe((response: any) => {
        this.updateContainmentObjects(response);
        this.onObjectContainmentChange();
      });
    } else {
      this.lmLplEditorService.getSecurableObjectsList(this.securableType, this.containment).subscribe((response: any) => {
        this.updateSecurableObjects(response);
        this.rebuildPreview();
      });
    }
  }

  onObjectContainmentChange() {
    switch (this.securableType) {
      case 'Field':
        this.lmLplEditorService.getSecurableObjectsList('Field', this.containment).subscribe((response: any) => {
          this.updateSecurableObjects(response);
          this.rebuildPreview();
        });
        break;
      case 'MenuItem':
        this.lmLplEditorService.getSecurableObjectsList('MenuItem', this.containment).subscribe((response: any) => {
          this.updateSecurableObjects(response);
          this.rebuildPreview();
        });
        break;
    }
  }

  onActionContainmentChange() {
    const secObjectName = this.containment?.trim().length > 0 ? this.containment : 'Type';
    this.lmLplEditorService.getSecurableActionsList(secObjectName, this.snippet).subscribe((response: any) => {
      this.updateSwapList(response);
      this.rebuildPreview();
    });
  }

  onConditionBuilderContainmentChange() {
    this.filterBuilder?.reset();
  }

  onFieldsContainmentChange() {
    this.lmLplEditorService.getSecurableObjectsList('AllFields', this.containment).subscribe((response: any) => {
      this.updateSwapList(response);
      this.rebuildPreview();
    });
  }

  onSecurableObjectChange() {
    this.rebuildPreview();
  }

  onSwapListUpdated(event: SohoSwapListSwapUpdateEvent) {
    this.ngZone.run(() => this.rebuildPreview());
  }

  copyToClipboard(previewComponent) {
    previewComponent.textarea.element.select();
    document.execCommand('copy');
    previewComponent.element.nativeElement.setSelectionRange(0, 0);
  }

  buildLpl(): string {
    if (this.options?.request === 'conditionBuilder') {
      return this.lmFilterBuilderService.convertFilterRowCriteria(this.filterBuilder.filterRows, this.filterBuilder.matchOption);
    } else {
      return this.preview;
    }
  }

  validateLpl(): Observable<boolean>  {
    if (this.options?.request !== 'conditionBuilder') {
      return of(true);
    }
    return this.filterBuilder.validateFilterConditions();
  }

  private initializeVariables() {
    this.useCompactFormLayout = this.lmGlobalPersonalizationService.getGlobalPersonalization().useCompactFormLayout;

    switch (this.options?.request) {
      case 'conditionBuilder':
        this.initializeConditionBuilderRequest();
        break;
      case 'actions':
        this.initializeActionRequest();
        break;
      case 'objects':
        this.initializeObjectRequest();
        break;
      case 'fields':
        this.initializeFieldRequest();
        break;
      case 'ontology':
        this.initializeOntologyRequest();
        break;
      case 'snippet':
        this.initializeSnippetRequest();
        break;
    }
  }

  private initializeSnippetRequest() {
    this.ruleTypeObjects = [];
    this.ruleTypeObjects.push({ value: 'object', label: Soho.Locale.translate('LPL_EDITOR_OBJECT_RULE') });
    this.ruleTypeObjects.push({ value: 'type', label:  Soho.Locale.translate('LPL_EDITOR_TYPE_RULE') });
    this.ruleType = 'object';

    this.onSnippetRuleTypeChange();
  }

  private initializeObjectRequest() {
    const securableTypeCtx = this.options?.rule?.type === 'object' ? this.options.rule.ruleTypeName as SecClassType : undefined;
    this.buildObjectTypes();

    if (securableTypeCtx) {
      this.securableType = securableTypeCtx;
    } else {
      this.securableType = this.securableTypeObjects[0].value;
    }

    this.onObjectTypeChange();
  }

  private initializeActionRequest() {
    const ruleTypeCtx = this.options?.rule?.type === 'type' ? 'type' : 'object';
    this.availableItemsHeader = Soho.Locale.translate('LPL_EDITOR_AvailableActions');
    this.selectedItemsHeader = Soho.Locale.translate('LPL_EDITOR_SelectedActions');
    this.ruleTypeObjects = [];
    this.ruleTypeObjects.push({ value: 'object', label: Soho.Locale.translate('LPL_EDITOR_OBJECT_RULE') });
    this.ruleTypeObjects.push({ value: 'type', label: Soho.Locale.translate('LPL_EDITOR_TYPE_RULE') });
    this.ruleType = ruleTypeCtx;

    this.onActionRuleTypeChange(true);
  }

  private initializeFieldRequest() {
    this.availableItemsHeader = Soho.Locale.translate('LPL_EDITOR_AvailableFields');
    this.selectedItemsHeader = Soho.Locale.translate('LPL_EDITOR_SelectedFields');
    this.hasContainment = true;
    this.containmentLabel = Soho.Locale.translate('LPL_EDITOR_BusinessClass');

    this.lmLplEditorService.getSecurableObjectsList('BusinessClass', this.containment).subscribe((response: any) => {
      this.updateContainmentObjects(response, true);
      this.onFieldsContainmentChange();
    });
  }

  private initializeOntologyRequest() {
    this.availableItemsHeader = Soho.Locale.translate('LPL_EDITOR_AvailableOntology');
    this.selectedItemsHeader = Soho.Locale.translate('LPL_EDITOR_SelectedOntology');
    this.lmLplEditorService.getSecurableObjectsList('BusinessClass', '').subscribe((response: any) => {
      this.updateSwapList(response);
      this.rebuildPreview();
    });
  }

  private initializeConditionBuilderRequest() {
    const baseUrl = this.lmUrlUtilService.getBaseUrl(true);
    this.fieldLookupUrl = this.lmUrlUtilService.updateContextSessionKeyParams(baseUrl + '/list/{resourceId}');
    this.fieldSearchUrl = this.lmUrlUtilService.updateContextSessionKeyParams(baseUrl + '/search/{resourceId}');
    this.lplValidationUrl = this.lmUrlUtilService.updateContextSessionKeyParams(baseUrl+ '/config/{resourceId}');
    this.containmentLabel = Soho.Locale.translate('LPL_EDITOR_BusinessClass');

    this.lmLplEditorService.getSecurableObjectsList('BusinessClass', this.containment).subscribe((response: any) => {
      this.updateContainmentObjects(response, true);
      this.onConditionBuilderContainmentChange();
    });
  }


  private getContainmentContext(): string {
    if (this.options?.rule?.objectName) {
      if (this.options.rule.ruleTypeName === 'Field') {
        return this.options.rule.objectName.substring(0, this.options.rule.objectName.indexOf('.'));
      } else {
        return this.options.rule.objectName;
      }
    } else {
      return undefined;
    }
  }

  private initializeObjectLabel() {
    switch (this.securableType) {
      case 'DataArea':
        this.securableObjectLabel = Soho.Locale.translate('LPL_EDITOR_DataArea');
        break;
      case 'BusinessClass':
        this.securableObjectLabel = Soho.Locale.translate('LPL_EDITOR_BusinessClass');
        break;
      case 'BusinessTask':
        this.securableObjectLabel = Soho.Locale.translate('LPL_EDITOR_BusinessTask');
        break;
      case 'Module':
        this.securableObjectLabel = Soho.Locale.translate('LPL_EDITOR_Module');
        break;
      case 'Field':
        this.securableObjectLabel = Soho.Locale.translate('LPL_EDITOR_Field');
        break;
        return;
      case 'WebApp':
        this.securableObjectLabel = Soho.Locale.translate('LPL_EDITOR_WebApp');
        break;
      case 'Executable':
        this.securableObjectLabel = Soho.Locale.translate('LPL_EDITOR_executable');
        break;
      case 'KeyField':
        this.securableObjectLabel = Soho.Locale.translate('LPL_EDITOR_KeyField');
        break;
      case 'Menu':
        this.securableObjectLabel = Soho.Locale.translate('LPL_EDITOR_Menu');
        break;
      case 'MenuItem':
        this.securableObjectLabel = Soho.Locale.translate('LPL_EDITOR_MenuItem');
        break;
    }
  }

  private buildSnippets() {
    this.snippetObjects = [];

    if (this.ruleType === 'type') {
      this.snippetObjects.push({ value: 'DataArea', label: Soho.Locale.translate('LPL_EDITOR_DataArea') });
      this.snippetObjects.push({ value: 'BusinessClass', label: Soho.Locale.translate('LPL_EDITOR_BusinessClass') });
      this.snippetObjects.push({ value: 'KeyField', label: Soho.Locale.translate('LPL_EDITOR_KeyField') });
      this.snippetObjects.push({ value: 'Field', label: Soho.Locale.translate('LPL_EDITOR_Field') });
      this.snippetObjects.push({ value: 'FieldGroup', label: Soho.Locale.translate('LPL_EDITOR_FieldGroups') });
      this.snippetObjects.push({ value: 'BusinessTask', label: Soho.Locale.translate('LPL_EDITOR_BusinessTask') });
      this.snippetObjects.push({ value: 'WebApp', label: Soho.Locale.translate('LPL_EDITOR_WebApp') });
      this.snippetObjects.push({ value: 'Menu', label: Soho.Locale.translate('LPL_EDITOR_Menu') });
      this.snippetObjects.push({ value: 'MenuItem', label: Soho.Locale.translate('LPL_EDITOR_MenuItem') });
      this.snippetObjects.push({ value: 'Module', label: Soho.Locale.translate('LPL_EDITOR_Module') });
      this.snippetObjects.push({ value: 'Executable', label: Soho.Locale.translate('LPL_EDITOR_executable') });
    }

    if (this.ruleType === 'object') {
      this.snippetObjects.push({ value: 'DataArea', label: Soho.Locale.translate('LPL_EDITOR_DataArea') });
      this.snippetObjects.push({ value: 'BusinessClass', label: Soho.Locale.translate('LPL_EDITOR_BusinessClass') });
      this.snippetObjects.push({ value: 'KeyField', label: Soho.Locale.translate('LPL_EDITOR_KeyField') });
      this.snippetObjects.push({ value: 'Field', label: Soho.Locale.translate('LPL_EDITOR_Field')  });
      this.snippetObjects.push({ value: 'AllFields', label: Soho.Locale.translate('LPL_EDITOR_AllFields') });
      this.snippetObjects.push({ value: 'BusinessTask', label: Soho.Locale.translate('LPL_EDITOR_BusinessTask') });
      this.snippetObjects.push({ value: 'WebApp', label: Soho.Locale.translate('LPL_EDITOR_WebApp') });
      this.snippetObjects.push({ value: 'Menu', label: Soho.Locale.translate('LPL_EDITOR_Menu') });
      this.snippetObjects.push({ value: 'MenuItem', label: Soho.Locale.translate('LPL_EDITOR_MenuItem') });
      this.snippetObjects.push({ value: 'Module', label: Soho.Locale.translate('LPL_EDITOR_Module') });
      this.snippetObjects.push({ value: 'Executable', label: Soho.Locale.translate('LPL_EDITOR_executable') });
    }

  }

  private buildObjectTypes() {
    this.securableTypeObjects = [];
    this.securableTypeObjects.push({ value: 'DataArea', label: Soho.Locale.translate('LPL_EDITOR_DataArea') });
    this.securableTypeObjects.push({ value: 'BusinessClass', label: Soho.Locale.translate('LPL_EDITOR_BusinessClass') });
    this.securableTypeObjects.push({ value: 'KeyField', label: Soho.Locale.translate('LPL_EDITOR_KeyField') });
    this.securableTypeObjects.push({ value: 'Field', label: Soho.Locale.translate('LPL_EDITOR_Field')  });
    this.securableTypeObjects.push({ value: 'BusinessTask', label: Soho.Locale.translate('LPL_EDITOR_BusinessTask') });
    this.securableTypeObjects.push({ value: 'WebApp', label: Soho.Locale.translate('LPL_EDITOR_WebApp') });
    this.securableTypeObjects.push({ value: 'Menu', label: Soho.Locale.translate('LPL_EDITOR_Menu') });
    this.securableTypeObjects.push({ value: 'MenuItem', label: Soho.Locale.translate('LPL_EDITOR_MenuItem') });
    this.securableTypeObjects.push({ value: 'Module', label: Soho.Locale.translate('LPL_EDITOR_Module')  });
    this.securableTypeObjects.push({ value: 'Executable', label: Soho.Locale.translate('LPL_EDITOR_executable') });
  }

  private updateContainmentObjects(response: Array<string>, initialization?: boolean) {
    const containmentCtx = initialization ? this.getContainmentContext() : undefined;
    this.containmentObjects = [];
    this.containment = undefined;

    if (Array.isArray(response)) {
      response.forEach((item) => {
        if (containmentCtx && containmentCtx === item) {
          this.containment = item;
        }
        this.containmentObjects.push({ value: item, label: item });
      });

      if (!this.containment && this.containmentObjects.length > 0) {
        this.containment =  this.containmentObjects[0].value;
      }
    }
  }

  private updateSecurableObjects(response: Array<string>) {
    this.securableObjects = [];
    this.securableObject = undefined;

    if (Array.isArray(response)) {
      response.forEach((item) => this.securableObjects.push({ value: item, label: item }));
    }
  }

  private updateSwapList(response: Array<string>) {
    this.availableItems = [];
    this.currentItems = [];

    if (Array.isArray(response)) {
      response.forEach((action) => {
        if (this.options?.request === 'ontology') {
          const keyfieldOntology = action + ' BusinessClass'; // this is not translated!!!
          const busClassOntology = action + ' KeyField';      // this is not translated!!!
          this.availableItems.push({ id: keyfieldOntology, value: keyfieldOntology, text: keyfieldOntology });
          this.availableItems.push({ id: busClassOntology, value: busClassOntology, text: busClassOntology });
        } else {
          this.availableItems.push({ id: action, value: action, text: action });
        }
      });
    }
  }

  private rebuildPreview() {
    switch (this.options?.request) {
      case 'snippet':
        this.rebuildSnippetPreview();
        break;
      case 'objects':
        this.rebuildObjectPreview();
        break;
      case 'actions':
      case 'fields':
      case 'ontology':
        this.rebuildListPreview();
        break;
      default:
        this.preview = '';
    }
  }

  private rebuildSnippetPreview() {
    const ruleType: SecClassRuleType = this.ruleType;
    const snippet: SecClassObject | SecClassType = this.snippet as SecClassObject | SecClassType;
    this.preview = this.lmLplEditorService.getSecuritySnippet(ruleType, snippet);
  }

  private rebuildObjectPreview() {
    if (this.securableType === 'Field') {
      this.preview = this.securableObject?.length > 0 ?  this.containment + '.' + this.securableObject : '';
    } else {
      this.preview = this.securableObject;
    }
  }

  private rebuildListPreview() {
    const temp = Array<string>();
    this.swapListComponent?.selectedItems.forEach(value => temp.push(value.value));
    this.preview = temp.join(', ');
  }
}
